import styled from 'styled-components';
import { colors, sizes, fontSizes, radiuses, shadows, sizesRaw } from '../ui';

export const Wrapper = styled.div`
  @keyframes skeleton-shimmer {
    from {
      background-position: 100% 0;
    }

    to {
      background-position: -100% 0;
    }
  }

  .order-skeletons {
    .order-skeleton {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: ${sizes.controlHeight};
    }

    .order-skeleton-copy {
      display: flex;
      flex: 1;
      min-width: 0;
      flex-direction: column;
      gap: ${sizes.s2};
    }

    .skeleton-block {
      display: block;
      border-radius: ${radiuses.r1};
      background: linear-gradient(
        90deg,
        ${colors.neutral200} 25%,
        ${colors.neutral100} 50%,
        ${colors.neutral200} 75%
      );
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.2s ease-in-out infinite;
    }

    .skeleton-title {
      width: min(52%, ${sizes.s11});
      height: ${sizes.s3};
    }

    .skeleton-timestamp {
      width: ${sizes.s9};
      height: ${sizes.s2};
    }
  }

  .status-block {
    margin: ${sizes.s4} 0;
    padding: ${sizes.s3} ${sizes.s4};
    border-radius: ${radiuses.r3};
    background-color: #fff;
    box-shadow: ${shadows.s1};
    text-align: center;
    font-size: ${fontSizes.s3};
    color: ${colors.neutral700};

    &.status-empty {
      border: 1px dashed ${colors.neutral300};
      background-color: ${colors.neutral100};
    }

    &.status-error {
      border: 1px solid ${colors.danger200};
      background-color: ${colors.danger100};
      color: ${colors.danger700};
    }

    .details {
      display: block;
      margin-top: ${sizes.s1};
      font-size: ${fontSizes.s2};
    }

    .retry-button {
      margin-top: ${sizes.s3};
      padding: ${sizes.s1} ${sizes.s3};
      border-radius: ${radiuses.r2};
      border: none;
      background-color: ${colors.primary500};
      color: #fff;
      font-size: ${fontSizes.s3};
      cursor: pointer;
      transition: opacity 150ms ease, transform 150ms ease;

      &:hover:not(:disabled) {
        opacity: 0.9;
      }

      &:active:not(:disabled) {
        transform: translateY(1px);
      }

      &:disabled {
        opacity: 0.6;
        cursor: default;
      }
    }
  }

  .packages {
    font-size: ${fontSizes.s3};

    table.packages {
      margin: ${sizes.s5} 0px 0px;

      td {
        padding: 2px ${sizes.s2};

        &:first-child {
          padding-left: 0px;
        }

        font-size: ${fontSizes.s3};

        &.caption {
          max-width: ${sizes.s11};
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &.right {
          text-align: right;
        }

        &.center {
          text-align: center;
        }
      }

      .img-container {
        padding: 0;
        border: 0;
        background: transparent;
        cursor: pointer;

        &:hover {
          opacity: 0.7;
        }

        transition: opacity 300ms ease;

        &:focus-visible {
          outline: 2px solid ${colors.primary500};
          outline-offset: ${sizes.s1};
        }
      }
    }

    main {
      overflow-x: scroll;
    }

    .package-cards {
      display: none;
    }

    footer {
      margin-top: ${sizes.s4};
      display: flex;
      justify-content: flex-end;

      button {
        background: none;
        border: none;
        padding: 0;
        font-size: inherit;
        font-family: inherit;
        color: ${colors.neutral700};
        text-decoration: underline;
        cursor: pointer;

        &:hover {
          color: ${colors.neutral900};
        }

        transition: color 150ms ease;
      }
    }
  }

  .photos-container {
    display: flex;
    column-gap: ${sizes.s2};
  }

  ul.orders {
    margin: 0px;
    padding: 0px;
    list-style-type: none;

    & > li {
      padding: ${sizes.s3} ${sizes.s4};
      box-shadow: ${shadows.s2};
      border-radius: ${radiuses.r3};
      background-color: #fff;

      .order-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
        transition: opacity 300ms ease;

        &:focus-visible {
          outline: 2px solid ${colors.primary500};
          outline-offset: ${sizes.s1};
        }

        &:hover,
        &:active {
          opacity: 0.7;
        }

        .left-side {
          h3 {
            font-size: ${fontSizes.s4};
            margin: 0px;
          }

          .timestamp {
            color: ${colors.neutral600};
            font-size: ${fontSizes.s2};
          }
        }

        .right-side {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          row-gap: ${sizes.s2};

          .labels {
            display: flex;

            .label {
              &:not(:first-child) {
                margin-left: ${sizes.s1};
              }
            }
          }

          .circles {
            display: flex;

            .circle {
              height: ${sizes.s4};
              width: ${sizes.s4};
              border-radius: ${sizesRaw.s4 / 2}px;

              &:not(:first-child) {
                margin-left: ${sizes.s1};
              }

              &.circle-gray {
                background-color: ${colors.neutral300};
              }

              &.circle-green {
                background-color: ${colors.success600};
              }
            }
          }
        }
      }

      margin-bottom: ${sizes.s4};
    }
  }

  ul.packages {
    margin: ${sizes.s4} 0px 0px;
    padding: 0px;
    list-style-type: none;

    & > li {
      margin-bottom: 8px;
      font-size: ${fontSizes.s3};
    }
  }

  /* Pagination block */
  .pagination {
    margin-top: ${sizes.s5};
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: ${sizes.s3};
    color: ${colors.neutral800};

    .page-info {
      font-size: ${fontSizes.s3};
      color: ${colors.neutral700};
      display: inline-flex;
      align-items: center;
      column-gap: ${sizes.s2};
    }

    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: ${sizes.s1} ${sizes.s3};
      background-color: #fff;
      border: 1px solid ${colors.neutral300};
      border-radius: ${radiuses.r2};
      color: ${colors.neutral900};
      font-size: ${fontSizes.s3};
      box-shadow: ${shadows.s1};
      cursor: pointer;
      transition: background-color 150ms ease, color 150ms ease,
        opacity 150ms ease;

      &:hover:not(:disabled) {
        background-color: ${colors.neutral100};
      }

      &:active:not(:disabled) {
        background-color: ${colors.neutral200};
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }
    }

    .limit-control {
      display: inline-flex;
      align-items: center;
      column-gap: ${sizes.s2};

      label {
        color: ${colors.neutral700};
        font-size: ${fontSizes.s3};
      }

      select {
        height: ${sizes.controlHeightSmall};
        padding: 0 ${sizes.s2};
        border: 1px solid ${colors.neutral300};
        border-radius: ${radiuses.r2};
        background-color: #fff;
        color: ${colors.neutral900};
        box-shadow: ${shadows.s1};
      }
    }

    /* Tiny inline spinner to indicate background page loading */
    .small-spinner {
      width: 12px;
      height: 12px;
      border: 2px solid ${colors.neutral300};
      border-top-color: ${colors.neutral700};
      border-radius: 50%;
      display: inline-block;
      animation: spin 600ms linear infinite;
    }
  }

  /* Responsive tweaks for mobile */
  @media (max-width: 600px) {
    .packages {
      .package-table-scroll {
        display: none;
      }

      .package-cards {
        display: grid;
        gap: ${sizes.s2};
        margin-top: ${sizes.s4};
      }

      .package-card {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: ${sizes.s3};
        padding: ${sizes.s3};
        border-radius: ${radiuses.r2};
        background-color: ${colors.neutral100};
      }

      .package-card-media {
        min-width: ${sizes.controlHeight};

        .photos-container {
          min-height: ${sizes.controlHeight};
          align-items: center;
        }

        .img-container {
          padding: 0;
          border: 0;
          background: transparent;
        }

        .img-container img {
          width: ${sizes.controlHeight};
          height: ${sizes.controlHeight};
          border-radius: ${radiuses.r1};
          object-fit: cover;
        }
      }

      .package-card-body {
        min-width: 0;

        h4 {
          margin: 0 0 ${sizes.s2};
          color: ${colors.neutral900};
          font-size: ${fontSizes.s4};
          overflow-wrap: anywhere;
        }
      }

      .package-card-metrics {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: ${sizes.s2};
        margin: 0 0 ${sizes.s3};

        dt {
          color: ${colors.neutral600};
          font-size: ${fontSizes.s2};
        }

        dd {
          margin: 2px 0 0;
          color: ${colors.neutral800};
          font-size: ${fontSizes.s3};
          font-weight: 700;
        }
      }

      .package-card-statuses {
        display: flex;
        flex-wrap: wrap;
        gap: ${sizes.s1};
      }
    }

    .pagination {
      flex-wrap: wrap;
      row-gap: ${sizes.s2};
      column-gap: ${sizes.s2};

      .btn {
        padding: ${sizes.s1} ${sizes.s2};
        font-size: ${fontSizes.s2};
      }

      .page-info {
        font-size: ${fontSizes.s2};
      }

      .limit-control {
        width: 100%;
        justify-content: center;

        label {
          margin-right: ${sizes.s1};
        }
        select {
          height: ${sizes.controlHeightTiny};
          font-size: ${fontSizes.s2};
        }
      }
    }
  }

  .cabinet-link-card {
    margin: ${sizes.s2} 0;
    padding: ${sizes.s7} ${sizes.s6};
    border-radius: ${radiuses.r3};
    background-color: #fff;
    box-shadow: ${shadows.s2};
    border: 1px solid ${colors.neutral300};
    text-align: left;

    .eyebrow {
      margin: 0 0 ${sizes.s1};
      color: ${colors.primary700};
      font-size: ${fontSizes.s2};
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    h2 {
      font-size: ${fontSizes.s7};
      margin: 0 0 ${sizes.s2} 0;
      color: ${colors.neutral900};
    }

    p {
      font-size: ${fontSizes.s3};
      color: ${colors.neutral600};
      line-height: 1.5;
      margin: 0 0 ${sizes.s4} 0;

      code {
        background-color: ${colors.neutral200};
        padding: 2px 6px;
        border-radius: ${radiuses.r1};
        font-family: monospace;
        font-size: 0.9em;
        color: ${colors.primary700};
        overflow-wrap: anywhere;
      }
    }

    .input-group {
      display: grid;
      row-gap: ${sizes.s2};
      width: 100%;

      label {
        color: ${colors.neutral800};
        font-size: ${fontSizes.s3};
        font-weight: 700;
      }

      .form-controls {
        display: flex;
        gap: ${sizes.s2};
      }

      input {
        flex: 1 1 auto;
        min-width: 0;
        width: 100%;
        height: ${sizes.controlHeightLarge};
        padding: 0 ${sizes.s4};
        border: 1px solid ${colors.neutral300};
        border-radius: ${radiuses.r2};
        font-size: ${fontSizes.s3};
        font-family: inherit;
        background-color: #fff;
        color: ${colors.neutral900};
        box-shadow: ${shadows.s1};
        transition: border-color 150ms ease, box-shadow 150ms ease;

        &:focus {
          outline: none;
          border-color: ${colors.primary500};
          box-shadow: 0 0 0 2px ${colors.primary200};
        }
      }

      .tracking-submit {
        flex: 0 0 auto;
        height: ${sizes.controlHeightLarge};
        white-space: nowrap;
      }

      .input-hint {
        margin: 0;
        color: ${colors.neutral600};
        font-size: ${fontSizes.s2};

        code {
          padding: 0;
          background: none;
        }
      }

      [role='alert'] {
        margin: 0;
        color: ${colors.danger700};
        font-size: ${fontSizes.s3};
      }
    }

    @media (max-width: 600px) {
      padding: ${sizes.s4};

      h2 {
        font-size: ${fontSizes.s6};
      }

      .input-group .form-controls {
        flex-direction: column;
      }

      .input-group .tracking-submit {
        width: 100%;
      }
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
